import React, { FocusEvent, MouseEvent, useRef, useState } from 'react'
import dayjs from 'utils/dayjs'

import { useTranslation } from 'language/translator'

import { DateCalendarProps } from 'components_new/atoms/DateCalendar'
import Icon from 'components_new/atoms/Icon'
import TextField, { TextFieldProps } from 'components_new/atoms/TextField'

import DatePopover from 'components_new/molecules/DatePopover'
import GenericListItem from 'components_new/molecules/GenericListItem'

import { getParsedDate } from 'utils/dateParser'

interface Props {
  closeOnSelect?: boolean
  DateCalendarProps: Omit<DateCalendarProps, 'value'>
  TextFieldProps: Omit<TextFieldProps, 'InputProps' | 'onFocus' | 'value'>
  value: dayjs.Dayjs | null | string
  variant?: 'field' | 'text'
}

const SelectDate = (props: Props) => {
  const {
    closeOnSelect,
    DateCalendarProps,
    TextFieldProps,
    value,
    variant = 'field'
  } = props
  const { sx, ...rest } = TextFieldProps

  const { lang } = useTranslation()

  const inputRef = useRef<HTMLElement | null>(null)
  const popoverPaperRef = useRef<HTMLElement | null>(null)

  const [open, setOpen] = useState<boolean>(false)

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget

    if (popoverPaperRef.current?.contains(next)) return

    handleClose()
  }

  const handleClickText = (event: MouseEvent<HTMLElement>) => {
    inputRef.current = event.currentTarget
    handleOpen()
  }

  const getTextColor = () => {
    if (TextFieldProps.error) return 'error.main'
    if (!value) return 'text.disabled'

    return undefined
  }

  const stringValue = value?.toString()

  return (
    <>
      {variant === 'text' ? (
        <GenericListItem
          disabled={TextFieldProps.disabled}
          disablePadding={true}
          icon={{
            color: TextFieldProps.error ? 'error' : 'disabled',
            name: 'CalendarMonthOutlined'
          }}
          onClick={TextFieldProps.disabled ? undefined : handleClickText}
          size="small"
          sx={{ borderRadius: 1, px: 0.5, whiteSpace: 'nowrap', ...sx }}
          title={
            value
              ? getParsedDate(stringValue, lang)
              : TextFieldProps.placeholder ?? ''
          }
          TitleProps={{ color: getTextColor() }}
        />
      ) : (
        <TextField
          InputProps={{
            endAdornment: (
              <Icon
                color="disabled"
                fontSize="inherit"
                name="CalendarMonthOutlined"
                onClick={handleOpen}
              />
            ),
            readOnly: true,
            ref: inputRef,
            sx: {
              cursor: 'pointer'
            }
          }}
          onBlur={handleBlur}
          onClick={handleOpen}
          onFocus={handleOpen}
          size="small"
          sx={{
            cursor: 'pointer',
            '& input': { cursor: 'pointer' },
            '& .MuiInputAdornment-root': { cursor: 'pointer' },
            ...sx
          }}
          value={getParsedDate(stringValue, lang)}
          {...rest}
        />
      )}

      <DatePopover
        anchorEl={inputRef.current}
        closeOnSelect={closeOnSelect}
        DateCalendarProps={DateCalendarProps}
        onClose={handleClose}
        open={open}
        paperRef={popoverPaperRef}
        value={value}
      />
    </>
  )
}

export default SelectDate
